import { useMarketingViewSection, useMarketingViewRestoring } from '../components/MarketingViewsProvider';
import { useState, useEffect, useCallback, useRef, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import * as echarts from 'echarts';
import {
  siteDataApi,
  type AcqPeriod,
  type AcqTrendRow,
  type AcqQualityRow,
  type AcqDetailRow,
  type AcqCostRow,
  type AcqChannelOptions,
  type AcqParams,
} from '../api/siteData';
import { ReportExportButton } from '../components/ReportExportButton';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import '../components/DataUpdateNote.css';
import { useSiteShopName } from '../hooks/useSiteShopName';
import { useLazySection } from '../hooks/useLazySection';
import { getCurrentCalendarRange, MarketingDateRangePicker } from '../components/MarketingTimeRangeFilter';
import { MultiSelect } from '../components/MultiSelect';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { SortableHeader, type SortState } from '../components/SortableTable';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { useAppStore } from '../store/useAppStore';
import { formatSiteMetric, isSiteSensitiveMetric } from '../lib/siteDataSecurity';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import Pagination from '../components/Pagination';

const fmtInt = (v: number) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'));
const fmtNum = (v: number) => (v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtPct = (v: number) => (v == null ? '-' : (v * 100).toFixed(2) + '%');

// 本地日期格式化（避免 toISOString 的 UTC 时区偏移导致日期回退一天）
function fmtLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// 各周期的默认区间（与 BI 一致：日=昨天单日，月=本月，季=本季度，年=本年）
function rangeFor(period: AcqPeriod): { start: string; end: string } {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth(); // 0-based
  if (period === 'day') {
    return topDayRange('recent7');
  }
  if (period === 'month') return { start: fmtLocalDate(new Date(y, m, 1)), end: fmtLocalDate(new Date(y, m + 1, 0)) };
  if (period === 'quarter') {
    const qStart = Math.floor(m / 3) * 3;
    return { start: fmtLocalDate(new Date(y, qStart, 1)), end: fmtLocalDate(new Date(y, qStart + 3, 0)) };
  }
  return { start: fmtLocalDate(new Date(y, 0, 1)), end: fmtLocalDate(new Date(y, 11, 31)) }; // year
}

const PERIOD_TABS: { key: AcqPeriod; label: string }[] = [
  { key: 'day', label: '日' }, { key: 'month', label: '月' },
  { key: 'quarter', label: '季' }, { key: 'year', label: '年' },
];

const DEFAULT_ACQ_TABLE_PAGE_SIZE = 50;

type TopDayRangeKey = 'today' | 'yesterday' | 'recent7' | 'recent14' | 'recent30' | 'thisWeek' | 'thisMonth' | 'custom';

function topDayRange(key: Exclude<TopDayRangeKey, 'custom'>): { start: string; end: string } {
  const today = new Date();
  if (key === 'thisWeek' || key === 'thisMonth') return getCurrentCalendarRange(key, fmtLocalDate(today));
  if (key === 'today') return { start: fmtLocalDate(today), end: fmtLocalDate(today) };
  if (key === 'yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const date = fmtLocalDate(yesterday);
    return { start: date, end: date };
  }
  const days = key === 'recent14' ? 14 : key === 'recent30' ? 30 : 7;
  const start = new Date(today);
  start.setDate(today.getDate() - days + 1);
  return { start: fmtLocalDate(start), end: fmtLocalDate(today) };
}

const TOP_DAY_RANGE_TABS: { key: TopDayRangeKey; label: string }[] = [
  { key: 'yesterday', label: '昨天' },
  { key: 'recent7', label: '最近7天' },
  { key: 'recent14', label: '近14天' },
  { key: 'recent30', label: '近30天' },
  { key: 'thisWeek', label: '本周' },
  { key: 'thisMonth', label: '本月' },
  { key: 'custom', label: '自定义' },
];

type AcqDetailDimKey = 'one_level_channel' | 'two_level_channel' | 'three_level_channel' | 'source' | 'medium' | 'campaign' | 'date';
type AcqCostKind = 'cost' | 'layered' | 'detail';

function cycleTableSort(current: SortState, key: string): SortState {
  if (!current || current.key !== key) return { key, direction: 'asc' };
  if (current.direction === 'asc') return { key, direction: 'desc' };
  return null;
}

function tableSortParams(sort: SortState): Pick<AcqParams, 'sort_by' | 'sort_dir'> {
  return sort ? { sort_by: sort.key, sort_dir: sort.direction } : {};
}

const DEFAULT_DETAIL_DIMS: AcqDetailDimKey[] = ['one_level_channel', 'two_level_channel', 'three_level_channel', 'date'];

const DETAIL_DIM_OPTIONS: { key: AcqDetailDimKey; label: string }[] = [
  { key: 'one_level_channel', label: '一级渠道' },
  { key: 'two_level_channel', label: '二级渠道' },
  { key: 'three_level_channel', label: '三级渠道' },
  { key: 'source', label: '来源' },
  { key: 'medium', label: '媒介' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'date', label: '日期' },
];

const FALLBACK_ACQ_OPTIONS: AcqChannelOptions = {
  one_level: ['全部', 'Affiliate', 'Organic search', 'Social', 'other', '付费广告', '媒体PR', '用户营销', '红人营销', '未知'],
  two_level: [
    '全部', 'Affiliate', 'KOL', 'Media', 'apple_ads', 'applovin_ads', 'bing', 'bing_ads', 'criteo_ads', 'duckduckgo',
    'fb_ads', 'google', 'google_ads', 'influencer', 'other', 'push', 'redirect', 'referral', 'rtb_ads', 'snapchat_ads',
    'social organic', 'social owned', 'tiktok_ads', 'twitter_ads', 'yahoo', 'yandex', '短信', '邮件', 'GEO', 'aol',
    'ecosia', 'msn', 'naver', 'pinterest_ads', 'pr', 'qwant', 'rakuten', '未知',
  ],
  three_level: [
    '全部', 'KOL_tiktok', 'KOL_youtube', 'OS_bing', 'OS_duckduckgo', 'OS_google', 'OS_yahoo', 'OS_yandex',
    'apple_ads', 'applovin_ads', 'bing_ads', 'criteo_ads', 'fb_ads',
    'google_ads_brand', 'google_ads_discover', 'google_ads_display', 'google_ads_other', 'google_ads_paidsearch',
    'google_ads_pmax', 'google_ads_youtube', 'impact', 'inf_other', 'inf_tiktok', 'inf_youtube', 'organic_facebook',
    'organic_instagram', 'organic_linktr', 'organic_other', 'organic_pinterest', 'organic_reddit', 'organic_snapchat',
    'organic_threads', 'organic_tiktok', 'organic_youtube', 'other', 'owned_facebook', 'owned_instagram', 'owned_other',
    'owned_pinterest', 'owned_tiktok', 'owned_twitter', 'owned_youtube', 'pepperjam', 'push-系统', 'push-营销',
    'push_other', 'rtb_ads', 'Awin', 'Class A', 'Class C', 'GEO', 'KOL_instagram', 'Media_other', 'OS_aol',
    'OS_ecosia', 'OS_msn', 'OS_naver', 'OS_qwant', 'OS_rakuten', 'google_ads_shopping', 'inf_instagram',
    'pinterest_ads', 'pr', 'redirect', 'referral', 'shareasale', 'snapchat_ads', 'tiktok_ads', 'twitter_ads',
    '短信_other', '短信-系统-通知类', '短信-系统-营销类', '短信-营销', '未知', '邮件_other', '邮件-系统-通知类',
    '邮件-系统-营销类', '邮件-营销',
  ],
  source: ['全部'],
  medium: ['全部'],
  campaign: ['全部'],
};

type UnifiedAcqFilters = {
  one_level_channel: string;
  two_level_channel: string;
  three_level_channel: string;
  source: string;
  medium: string;
  campaign: string;
};
type AcqFilterExclusions = Partial<Record<keyof UnifiedAcqFilters, boolean>>;
const acqExclusionParam = (filters: AcqFilterExclusions) => Object.entries(filters).filter(([, enabled]) => enabled).map(([key]) => key).join(',');
const DEFAULT_UNIFIED_FILTERS: UnifiedAcqFilters = {
  one_level_channel: '全部',
  two_level_channel: '全部',
  three_level_channel: '全部',
  source: '全部',
  medium: '全部',
  campaign: '全部',
};

function cleanAcqFilters(filters: UnifiedAcqFilters, exclusions: AcqFilterExclusions = {}): Partial<AcqParams> {
  const out: Partial<AcqParams> = {};
  const oneLevel = filters.one_level_channel?.trim();
  const twoLevel = filters.two_level_channel?.trim();
  const threeLevel = filters.three_level_channel?.trim();
  const source = filters.source?.trim();
  const medium = filters.medium?.trim();
  const campaign = filters.campaign?.trim();
  if (oneLevel && oneLevel !== '全部') out.one_level_channel = oneLevel;
  if (twoLevel && twoLevel !== '全部') out.two_level_channel = twoLevel;
  if (threeLevel && threeLevel !== '全部') out.three_level_channel = threeLevel;
  if (source && source !== '全部') out.source = source;
  if (medium && medium !== '全部') out.medium = medium;
  if (campaign && campaign !== '全部') out.campaign = campaign;
  const exclude = acqExclusionParam(exclusions);
  if (exclude) out.exclude = exclude;
  return out;
}

function withFallbackAll(options: string[] | undefined): string[] {
  if (!options?.length) return ['全部'];
  return options.includes('全部') ? options : ['全部', ...options];
}

function detailDimLabel(key: AcqDetailDimKey): string {
  return DETAIL_DIM_OPTIONS.find(option => option.key === key)?.label ?? key;
}

function detailDimValue(row: AcqDetailRow, key: AcqDetailDimKey): string {
  const value = row.dimensions?.[key];
  if (value !== undefined && value !== null) return value;
  if (key === 'date') return row.date;
  if (key === 'one_level_channel') return row.channel;
  return '全部';
}

// ── echarts: 拉新趋势 (柱: 新客数/支付用户数 + 线: 新客占比) ───────────
function TrendChart({ rows, height = 360 }: { rows: AcqTrendRow[]; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    if (!chartRef.current) chartRef.current = echarts.init(ref.current);
    const cats = rows.map(r => r.channel);
    const rootStyles = getComputedStyle(document.documentElement);
    const formatCountLabel = (value: number | string) => Number(value ?? 0).toLocaleString('zh-CN');
    const formatRateLabel = (value: number | string) => `${(Number(value ?? 0) * 100).toFixed(2)}%`;
    chartRef.current.setOption({
      color: [
        rootStyles.getPropertyValue('--primary').trim(),
        rootStyles.getPropertyValue('--green').trim(),
        rootStyles.getPropertyValue('--amber').trim(),
      ],
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { bottom: 0, data: ['新客数', '支付用户数', '新客占比'] },
      grid: { left: 56, right: 56, top: 46, bottom: 48 },
      xAxis: { type: 'category', data: cats, axisLabel: { rotate: cats.length > 6 ? 30 : 0, fontSize: 11 } },
      yAxis: [
        { type: 'value', name: '人数' },
        { type: 'value', name: '占比', axisLabel: { formatter: (v: number) => (v * 100).toFixed(0) + '%' }, max: 1 },
      ],
      series: [
        {
          name: '新客数',
          type: 'bar',
          data: rows.map(r => r.new_user_num),
          label: { show: true, position: 'top', distance: 4, fontSize: 11, formatter: (params: { value: number | string }) => formatCountLabel(params.value) },
        },
        {
          name: '支付用户数',
          type: 'bar',
          data: rows.map(r => r.paid_user_num),
          label: { show: true, position: 'top', distance: 4, fontSize: 11, formatter: (params: { value: number | string }) => formatCountLabel(params.value) },
        },
        {
          name: '新客占比',
          type: 'line',
          yAxisIndex: 1,
          symbol: 'circle',
          symbolSize: 6,
          data: rows.map(r => r.new_user_rate),
          label: { show: true, position: 'top', distance: 8, fontSize: 11, formatter: (params: { value: number | string }) => formatRateLabel(params.value) },
          tooltip: { valueFormatter: (v: number) => formatRateLabel(v) },
        },
      ],
    }, true);
  }, [rows]);
  useEffect(() => { const f = () => chartRef.current?.resize(); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  useEffect(() => () => { chartRef.current?.dispose(); chartRef.current = null; }, []);
  return <div ref={ref} style={{ width: '100%', height }} />;
}

// ── echarts: 新客质量 (三组单价柱状) ─────────────────────────────────
function QualityChart({ rows, height = 340, userLevel }: { rows: AcqQualityRow[]; height?: number; userLevel: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (!chartRef.current) chartRef.current = echarts.init(ref.current);
    const cats = rows.map(r => r.two_level_channel || r.one_level_channel);
    const formatPriceLabel = (value: number | string) => fmtNum(Number(value ?? 0));
    chartRef.current.setOption({
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: (v: number) => (v == null ? '-' : v.toFixed(2)) },
      legend: { bottom: 0, data: ['新客单均价', '新客客单价', '新客首单单均价'] },
      grid: { left: 48, right: 24, top: 46, bottom: 48 },
      xAxis: { type: 'category', data: cats, axisLabel: { rotate: cats.length > 6 ? 30 : 0, fontSize: 11 } },
      yAxis: { type: 'value' },
      series: [
        { name: '新客单均价', type: 'bar', data: rows.map(r => r.new_avg_price), label: { show: true, position: 'top', distance: 4, fontSize: 10, formatter: (params: { value: number | string }) => formatPriceLabel(params.value) } },
        { name: '新客客单价', type: 'bar', data: rows.map(r => r.new_cust_price), label: { show: true, position: 'top', distance: 4, fontSize: 10, formatter: (params: { value: number | string }) => formatPriceLabel(params.value) } },
        { name: '新客首单单均价', type: 'bar', data: rows.map(r => r.new_first_avg), label: { show: true, position: 'top', distance: 4, fontSize: 10, formatter: (params: { value: number | string }) => formatPriceLabel(params.value) } },
      ],
    }, true);
  }, [rows]);
  useEffect(() => { const f = () => chartRef.current?.resize(); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  useEffect(() => () => { chartRef.current?.dispose(); chartRef.current = null; }, []);
  if (userLevel < 2) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 13 }}>暂无权限查看单价数据</div>;
  }
  return <div ref={ref} style={{ width: '100%', height }} />;
}

const tdNum: React.CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const thBase: React.CSSProperties = { background: 'var(--surface2)', whiteSpace: 'nowrap' };

function Card({ title, children, extra }: { title: string; children: ReactNode; extra?: ReactNode }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>{title}</span>
        {extra}
      </div>
      {children}
    </div>
  );
}

const DETAIL_COLS: { key: keyof AcqDetailRow; label: string; fmt: (v: number) => string }[] = [
  { key: 'paid_user_num', label: '支付用户数', fmt: fmtInt },
  { key: 'order_num', label: '支付订单数', fmt: fmtInt },
  { key: 'new_base_payable', label: '新客净销售额', fmt: fmtNum },
  { key: 'new_order_num', label: '新客支付订单数', fmt: fmtInt },
  { key: 'new_user_num', label: '新客数', fmt: fmtInt },
  { key: 'new_user_rate', label: '新客占比', fmt: fmtPct },
  { key: 'new_avg_price', label: '新客单均价', fmt: fmtNum },
  { key: 'new_cust_price', label: '新客客单价', fmt: fmtNum },
  { key: 'first_order_pay', label: '新客首单净销售额', fmt: fmtNum },
  { key: 'new_first_avg', label: '新客首单单均价', fmt: fmtNum },
];

// 花费/分层 表列定义
const COST_COLS: { key: keyof AcqCostRow; label: string; fmt: (v: number) => string }[] = [
  { key: 'spend', label: '账户花费', fmt: fmtNum },
  { key: 'uv', label: '访问UV', fmt: fmtInt },
  { key: 'uv_cost', label: '访问UV成本', fmt: fmtNum },
  { key: 'order_num', label: '支付订单数', fmt: fmtInt },
  { key: 'cvr', label: '访购率', fmt: fmtPct },
  { key: 'paid_user_num', label: '支付用户数', fmt: fmtInt },
  { key: 'new_base_payable', label: '新客净销售额', fmt: fmtNum },
  { key: 'new_order_num', label: '新客支付订单数', fmt: fmtInt },
  { key: 'new_user_num', label: '新客数', fmt: fmtInt },
  { key: 'new_user_spend', label: '新客花费', fmt: fmtNum },
  { key: 'new_acq_cost', label: '新客获客成本', fmt: fmtNum },
  { key: 'new_user_rate', label: '新客占比', fmt: fmtPct },
  { key: 'new_cust_price', label: '新客客单价', fmt: fmtNum },
  { key: 'new_avg_price', label: '新客单均价', fmt: fmtNum },
  { key: 'first_order_pay', label: '新客首单净销售额', fmt: fmtNum },
  { key: 'new_first_avg', label: '新客首单单均价', fmt: fmtNum },
];

const acqTrendExportColumns = [
  { key: 'channel', label: '一级渠道' },
  { key: 'new_user_num', label: '新客数' },
  { key: 'paid_user_num', label: '支付用户数' },
  { key: 'new_user_rate', label: '新客占比' },
];
const acqQualityExportColumns = [
  { key: 'one_level_channel', label: '一级渠道' },
  { key: 'two_level_channel', label: '二级渠道' },
  { key: 'new_user_num', label: '新客数' },
  { key: 'new_avg_price', label: '新客单均价' },
  { key: 'new_cust_price', label: '新客客单价' },
  { key: 'new_first_avg', label: '新客首单单均价' },
];

function acqDetailExportColumns(dims: AcqDetailDimKey[]) {
  return [
    ...dims.map(dim => ({ key: dim, label: detailDimLabel(dim) })),
    ...DETAIL_COLS.map(col => ({ key: col.key as string, label: col.label })),
  ];
}

function acqCostExportColumns(dimLabelText: string) {
  return [
    { key: 'dim', label: dimLabelText },
    { key: 'date', label: '日期' },
    ...COST_COLS.map(col => ({ key: col.key as string, label: col.label })),
  ];
}

export default function SiteChannelAcquisition() {
  const isRestoringView = useMarketingViewRestoring();
  const { shopName: site, brand } = useSiteShopName();
  const adContext = useAppStore(s => s.adContext);
  const userLevel = adContext?.data_level ?? 1;
  const isVooglam = brand.toLowerCase() === 'vooglam';
  const [period, setPeriod] = useState<AcqPeriod>('day');
  const init = rangeFor('day');
  const [startDate, setStartDate] = useState(init.start);
  const [endDate, setEndDate] = useState(init.end);
  const [topDayRangeKey, setTopDayRangeKey] = useState<TopDayRangeKey>('recent7');

  const [trend, setTrend] = useState<AcqTrendRow[]>([]);
  const [q1, setQ1] = useState<AcqQualityRow[]>([]);
  const [q2, setQ2] = useState<AcqQualityRow[]>([]);
  const [detail, setDetail] = useState<AcqDetailRow[]>([]);
  const [channelOptions, setChannelOptions] = useState<AcqChannelOptions>(FALLBACK_ACQ_OPTIONS);
  const [channelOptionsLoading, setChannelOptionsLoading] = useState(!isVooglam);
  const [channelOptionsError, setChannelOptionsError] = useState('');
  const [unifiedFilters, setUnifiedFilters] = useState<UnifiedAcqFilters>(DEFAULT_UNIFIED_FILTERS);
  const [unifiedExclusions, setUnifiedExclusions] = useState<AcqFilterExclusions>({});
  const [cost, setCost] = useState<AcqCostRow[]>([]);
  const [layered, setLayered] = useState<AcqCostRow[]>([]);
  const [costDetail, setCostDetail] = useState<AcqCostRow[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [q1Loading, setQ1Loading] = useState(false);
  const [q2Loading, setQ2Loading] = useState(false);
  const [error, setError] = useState('');
  const detailSectionRef = useRef<HTMLDivElement>(null);
  const costSectionRef = useRef<HTMLDivElement>(null);
  const [detailActivated, setDetailActivated] = useState(false);
  const [costActivated, setCostActivated] = useState(false);
  const q1Section = useLazySection(Boolean(site && !isVooglam));
  const q2Section = useLazySection(Boolean(site && !isVooglam));

  const [detailDims, setDetailDims] = useState<AcqDetailDimKey[]>(DEFAULT_DETAIL_DIMS);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(DEFAULT_ACQ_TABLE_PAGE_SIZE);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailSort, setDetailSort] = useState<SortState>(null);
  const [detailQueryVersion, setDetailQueryVersion] = useState(0);
  const [costLoading, setCostLoading] = useState(false);
  const [queryVersion, setQueryVersion] = useState(0);
  const [costError, setCostError] = useState('');
  const [costPages, setCostPages] = useState<Record<AcqCostKind, number>>({ cost: 1, layered: 1, detail: 1 });
  const [costPageSizes, setCostPageSizes] = useState<Record<AcqCostKind, number>>({ cost: DEFAULT_ACQ_TABLE_PAGE_SIZE, layered: DEFAULT_ACQ_TABLE_PAGE_SIZE, detail: DEFAULT_ACQ_TABLE_PAGE_SIZE });
  const [costTotals, setCostTotals] = useState<Record<AcqCostKind, number>>({ cost: 0, layered: 0, detail: 0 });
  const [costSorts, setCostSorts] = useState<Record<AcqCostKind, SortState>>({ cost: null, layered: null, detail: null });
  const [trendExportParams, setTrendExportParams] = useState<AcqParams | null>(null);
  const [q1ExportParams, setQ1ExportParams] = useState<AcqParams | null>(null);
  const [q2ExportParams, setQ2ExportParams] = useState<AcqParams | null>(null);
  const [detailExportSnapshot, setDetailExportSnapshot] = useState<{ params: AcqParams; dimensions: AcqDetailDimKey[] } | null>(null);
  const [costExportSnapshot, setCostExportSnapshot] = useState<{ params: AcqParams; sorts: Record<AcqCostKind, SortState> } | null>(null);
  useEffect(() => {
    if (isVooglam) {
      setChannelOptions(FALLBACK_ACQ_OPTIONS);
      setChannelOptionsLoading(false);
      setChannelOptionsError('');
      return;
    }
    let alive = true;
    setChannelOptionsLoading(true);
    setChannelOptionsError('');
    siteDataApi.getAcqOptions()
      .then(res => {
        if (!alive) return;
        setChannelOptions({
          one_level: withFallbackAll(res.data?.one_level),
          two_level: withFallbackAll(res.data?.two_level),
          three_level: withFallbackAll(res.data?.three_level),
          source: withFallbackAll(res.data?.source),
          medium: ['全部'],
          campaign: ['全部'],
        });
      })
      .catch((e) => {
        if (!alive) return;
        setChannelOptions(FALLBACK_ACQ_OPTIONS);
        setChannelOptionsError(e instanceof Error ? `筛选项加载失败：${e.message}` : '筛选项加载失败');
      })
      .finally(() => {
        if (alive) setChannelOptionsLoading(false);
      });
    return () => { alive = false; };
  }, [isVooglam]);

  // 切换周期时套用该周期默认区间
  const onPeriod = (p: AcqPeriod) => {
    setPeriod(p);
    const r = rangeFor(p);
    setStartDate(r.start); setEndDate(r.end);
    setTopDayRangeKey(p === 'day' ? 'recent7' : 'custom');
  };

  const onTopDayRange = (key: TopDayRangeKey) => {
    setTopDayRangeKey(key);
    if (key === 'custom') return;
    const r = topDayRange(key);
    setStartDate(r.start);
    setEndDate(r.end);
  };

  useEffect(() => {
    if (detailActivated) return;
    const node = detailSectionRef.current;
    if (!node || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setDetailActivated(true);
      observer.disconnect();
    }, { rootMargin: '360px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [detailActivated]);

  useEffect(() => {
    if (costActivated) return;
    const node = costSectionRef.current;
    if (!node || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setCostActivated(true);
      observer.disconnect();
    }, { rootMargin: '360px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [costActivated]);

  const toggleCostSort = useCallback((kind: AcqCostKind, key: string) => {
    setCostSorts(current => ({ ...current, [kind]: cycleTableSort(current[kind], key) }));
    setCostPages(current => ({ ...current, [kind]: 1 }));
  }, []);

  const toggleDetailSort = useCallback((key: string) => {
    setDetailSort(current => cycleTableSort(current, key));
    setDetailPage(1);
    setDetailQueryVersion(version => version + 1);
  }, []);

  const handleDetailDimensionsChange = useCallback((next: string[]) => {
    const normalized = DETAIL_DIM_OPTIONS
      .map(option => option.key)
      .filter(key => next.includes(key));
    if (normalized.length === 0) return;
    setDetailDims(normalized);
    setDetailSort(current => current && !normalized.includes(current.key as AcqDetailDimKey) && DETAIL_DIM_OPTIONS.some(option => option.key === current.key) ? null : current);
    setDetailPage(1);
    setDetailQueryVersion(version => version + 1);
  }, []);

  const fetchTrend = useCallback(async () => {
    if (!site) return;
    if (isVooglam) {
      setTrend([]);
      setTrendExportParams(null);
      setError('');
      setTrendLoading(false);
      return;
    }
    setTrendLoading(true); setError(''); setTrendExportParams(null);
    const filterParams = cleanAcqFilters(unifiedFilters, unifiedExclusions);
    const base: AcqParams = { site, start_date: startDate, end_date: endDate, period, ...filterParams };
    try {
      const result = await siteDataApi.getAcqTrend(base);
      setTrend(result.data?.list ?? []);
      setTrendExportParams({ ...base });
    } catch (e) {
      setTrendExportParams(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setTrendLoading(false);
    }
  }, [site, isVooglam, startDate, endDate, period, unifiedFilters, unifiedExclusions]);

  const fetchQ1 = useCallback(async () => {
    if (!site || isVooglam) {
      setQ1([]);
      setQ1ExportParams(null);
      setQ1Loading(false);
      return;
    }
    setQ1Loading(true); setError(''); setQ1ExportParams(null);
    const base: AcqParams = { site, start_date: startDate, end_date: endDate, period, ...cleanAcqFilters(unifiedFilters, unifiedExclusions) };
    try {
      const result = await siteDataApi.getAcqQuality({ ...base, level: 1 });
      setQ1(result.data?.list ?? []);
      setQ1ExportParams({ ...base, level: 1 });
    } catch (e) {
      setQ1ExportParams(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setQ1Loading(false);
    }
  }, [site, isVooglam, startDate, endDate, period, unifiedFilters, unifiedExclusions]);

  const fetchQ2 = useCallback(async () => {
    if (!site || isVooglam) {
      setQ2([]);
      setQ2ExportParams(null);
      setQ2Loading(false);
      return;
    }
    setQ2Loading(true); setError(''); setQ2ExportParams(null);
    const base: AcqParams = { site, start_date: startDate, end_date: endDate, period, ...cleanAcqFilters(unifiedFilters, unifiedExclusions) };
    try {
      const result = await siteDataApi.getAcqQuality({ ...base, level: 2 });
      setQ2(result.data?.list ?? []);
      setQ2ExportParams({ ...base, level: 2 });
    } catch (e) {
      setQ2ExportParams(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setQ2Loading(false);
    }
  }, [site, isVooglam, startDate, endDate, period, unifiedFilters, unifiedExclusions]);

  const fetchDetail = useCallback(async () => {
    if (!site || isVooglam) {
      setDetail([]);
      setDetailTotal(0);
      setDetailExportSnapshot(null);
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    setDetailExportSnapshot(null);
    try {
      const detailParams: AcqParams = {
        site,
        start_date: startDate,
        end_date: endDate,
        period,
        ...cleanAcqFilters(unifiedFilters, unifiedExclusions),
        dimensions: detailDims.join(','),
        page: detailPage,
        page_size: detailPageSize,
        ...tableSortParams(detailSort),
      };
      const result = await siteDataApi.getAcqDetail(detailParams);
      setDetail(result.data?.list ?? []);
      setDetailTotal(result.data?.total ?? 0);
      const { page: _page, page_size: _pageSize, ...exportParams } = detailParams;
      setDetailExportSnapshot({ params: exportParams, dimensions: [...detailDims] });
    } catch {
      setDetail([]);
      setDetailTotal(0);
      setDetailExportSnapshot(null);
    } finally {
      setDetailLoading(false);
    }
  }, [site, isVooglam, startDate, endDate, period, unifiedFilters, unifiedExclusions, detailDims, detailPage, detailPageSize, detailSort]);

  const fetchCost = useCallback(async () => {
    if (isRestoringView()) return;
    if (!site) return;
    if (isVooglam) {
      setCost([]);
      setLayered([]);
      setCostDetail([]);
      setCostTotals({ cost: 0, layered: 0, detail: 0 });
      setCostError('');
      setCostExportSnapshot(null);
      setCostLoading(false);
      return;
    }
    setCostLoading(true);
    setCostError('');
    setCostExportSnapshot(null);
    // 花费事实表只按顶部日期维度和日期范围查询，不应用渠道或 UTM 筛选。
    const base: AcqParams = {
      site,
      start_date: startDate,
      end_date: endDate,
      period,
    };
    try {
      const [c, l, cd] = await Promise.all([
        siteDataApi.getAcqCost({ ...base, kind: 'cost', page: costPages.cost, page_size: costPageSizes.cost, ...tableSortParams(costSorts.cost) }),
        siteDataApi.getAcqCost({ ...base, kind: 'layered', page: costPages.layered, page_size: costPageSizes.layered, ...tableSortParams(costSorts.layered) }),
        siteDataApi.getAcqCost({ ...base, kind: 'detail', page: costPages.detail, page_size: costPageSizes.detail, ...tableSortParams(costSorts.detail) }),
      ]);
      setCost(c.data?.list ?? []);
      setLayered(l.data?.list ?? []);
      setCostDetail(cd.data?.list ?? []);
      setCostTotals({
        cost: c.data?.total ?? 0,
        layered: l.data?.total ?? 0,
        detail: cd.data?.total ?? 0,
      });
      setCostExportSnapshot({ params: { ...base }, sorts: { ...costSorts } });
    } catch (e) {
      setCost([]); setLayered([]); setCostDetail([]);
      setCostTotals({ cost: 0, layered: 0, detail: 0 });
      setCostError(e instanceof Error ? `花费数据加载失败：${e.message}` : '花费数据加载失败');
      setCostExportSnapshot(null);
    } finally {
      setCostLoading(false);
    }
  }, [site, isVooglam, startDate, endDate, period, costPages, costPageSizes, costSorts, isRestoringView]);

  useEffect(() => { void fetchTrend(); }, [queryVersion, site]);
  useEffect(() => { if (q1Section.ready) void fetchQ1(); }, [q1Section.ready, queryVersion, site]);
  useEffect(() => { if (q2Section.ready) void fetchQ2(); }, [q2Section.ready, queryVersion, site]);
  useEffect(() => { if (detailActivated) void fetchDetail(); }, [detailActivated, queryVersion, detailQueryVersion, detailPage, detailPageSize, site]);
  useEffect(() => { if (site && costActivated) void fetchCost(); }, [costActivated, queryVersion, site, costPages, costPageSizes, costSorts]);

  const query = () => {
    setDetailPage(1);
    setCostPages({ cost: 1, layered: 1, detail: 1 });
    setQueryVersion(version => version + 1);
  };
  useMarketingViewSection('page', {
    label: '页面筛选', state: { period, startDate, endDate, topDayRangeKey, unifiedFilters, unifiedExclusions, detailDims, detailSort, costSorts },
    restore: value => { setPeriod(value.period); setStartDate(value.startDate); setEndDate(value.endDate); setTopDayRangeKey(value.topDayRangeKey); setUnifiedFilters(value.unifiedFilters); setUnifiedExclusions(value.unifiedExclusions); setDetailDims(value.detailDims); setDetailSort(value.detailSort); setCostSorts(value.costSorts); },
    loading: trendLoading || q1Loading || q2Loading || detailLoading || costLoading || channelOptionsLoading, query: () => { query(); },
    resolve: value => { if (value.period !== 'day' || value.topDayRangeKey === 'custom') return value; const range = topDayRange(value.topDayRangeKey); return { ...value, topDayRangeKey: value.topDayRangeKey === 'today' ? 'custom' as const : value.topDayRangeKey, startDate: range.start, endDate: range.end }; },
    afterQuery: () => { if (costActivated) void fetchCost(); },
    summary: startDate + ' ～ ' + endDate,
  });

  const reset = () => {
    const range = rangeFor('day');
    setPeriod('day');
    setStartDate(range.start);
    setEndDate(range.end);
    setTopDayRangeKey('recent7');
    setUnifiedFilters(DEFAULT_UNIFIED_FILTERS);
    setUnifiedExclusions({});
    setDetailPage(1);
    setCostPages({ cost: 1, layered: 1, detail: 1 });
    setCostSorts({ cost: null, layered: null, detail: null });
    setError('');
    setQueryVersion(version => version + 1);
  };

  if (isVooglam) {
    return (
      <div className="page-section visible">
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>当前品牌暂无渠道拉新效果监控</div>
          <div style={{ fontSize: 13 }}>该报表仅适用于 Zeelool，Vooglam 暂未在 BI 中配置此报表。</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-section visible">
      <div className="dashboard-sticky-filters">
      {/* 周期 + 日期 */}
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <span className="data-filter-inline-label">日期维度</span>
          <div className="page-tabs" style={{ marginBottom: 0 }}>
            {PERIOD_TABS.map(t => (
              <div key={t.key}
                className={`page-tab${period === t.key ? ' active' : ''}`}
                onClick={() => onPeriod(t.key)}>{t.label}</div>
            ))}
          </div>

          <span className="data-filter-inline-label">日期范围</span>
          {period === 'day' ? (
            <>
              <div className="page-tabs" style={{ marginBottom: 0 }}>
                {TOP_DAY_RANGE_TABS.map(t => (
                  <div
                    key={t.key}
                    className={`page-tab${topDayRangeKey === t.key ? ' active' : ''}`}
                    onClick={() => onTopDayRange(t.key)}
                  >
                    {t.label}
                  </div>
                ))}
              </div>
              {topDayRangeKey === 'custom' && <MarketingDateRangePicker start={startDate} end={endDate} max={fmtLocalDate(new Date())} onChange={({ start, end }) => { setTopDayRangeKey('custom'); setStartDate(start); setEndDate(end); }} />}
            </>
          ) : <MarketingDateRangePicker grain={period} start={startDate} end={endDate} max={fmtLocalDate(new Date())} onChange={({ start, end }) => { setStartDate(start); setEndDate(end); }} />}
        </div>
      </div>

      <div className="data-filter-card">
        <UnifiedFilterRow
          filters={unifiedFilters}
          options={channelOptions}
          optionsLoading={channelOptionsLoading}
          optionsError={channelOptionsError}
          onChange={setUnifiedFilters}
          exclusions={unifiedExclusions}
          onExclusionsChange={setUnifiedExclusions}
        />
        <DashboardQueryActions loading={trendLoading || q1Loading || q2Loading || detailLoading || costLoading} onReset={reset} onQuery={query} />
      </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: 'rgba(220,38,38,.08)', color: '#dc2626', fontSize: 13 }}>{error}</div>
      )}

      <Card
        title="渠道拉新趋势 & 新客质量"
        extra={<ReportExportButton reportKey="site_channel_acq_trend" title="渠道拉新趋势" fileName={`渠道拉新趋势_${trendExportParams?.start_date ?? ''}_${trendExportParams?.end_date ?? ''}.csv`} params={trendExportParams ?? {}} columns={acqTrendExportColumns} disabled={!trendExportParams} />}
      >
        <div style={{ position: 'relative', padding: 16 }}>
          {trend.length === 0 ? (trendLoading ? <TableLoadingState /> : <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>暂无数据</div>)
            : <TrendChart rows={trend} />}
          {trendLoading && trend.length > 0 && <TableLoadingOverlay />}
        </div>
      </Card>

      <div ref={q1Section.ref}>
      <Card
        title="一级渠道新客质量"
        extra={<ReportExportButton reportKey="site_channel_acq_quality" title="一级渠道新客质量" fileName={`一级渠道新客质量_${q1ExportParams?.start_date ?? ''}_${q1ExportParams?.end_date ?? ''}.csv`} params={q1ExportParams ?? {}} columns={acqQualityExportColumns} disabled={!q1ExportParams} />}
      >
        <div style={{ position: 'relative', padding: 16 }}>
          {q1.length === 0 ? (q1Loading ? <TableLoadingState /> : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</div>)
            : <QualityChart rows={q1} userLevel={userLevel} />}
          {q1Loading && q1.length > 0 && <TableLoadingOverlay />}
        </div>
      </Card>
      </div>

      <div ref={q2Section.ref}>
      <Card
        title="二级渠道新客质量"
        extra={<ReportExportButton reportKey="site_channel_acq_quality" title="二级渠道新客质量" fileName={`二级渠道新客质量_${q2ExportParams?.start_date ?? ''}_${q2ExportParams?.end_date ?? ''}.csv`} params={q2ExportParams ?? {}} columns={acqQualityExportColumns} disabled={!q2ExportParams} />}
      >
        <div style={{ position: 'relative', padding: 16 }}>
          {q2.length === 0 ? (q2Loading ? <TableLoadingState /> : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</div>)
            : <QualityChart rows={q2} userLevel={userLevel} />}
          {q2Loading && q2.length > 0 && <TableLoadingOverlay />}
        </div>
      </Card>
      </div>

      <div ref={detailSectionRef}>
        <Card
          title="明细表"
          extra={<ReportExportButton reportKey="site_channel_acq_detail" title="渠道拉新明细表" fileName={`渠道拉新明细表_${detailExportSnapshot?.params.start_date ?? ''}_${detailExportSnapshot?.params.end_date ?? ''}.csv`} params={detailExportSnapshot?.params ?? {}} columns={acqDetailExportColumns(detailExportSnapshot?.dimensions ?? [])} disabled={!detailExportSnapshot} />}
        >
          <div className="section-filter-panel">
            <div className="data-filter-row dimension-select-row">
              <span className="dimension-select-label">动态维度</span>
              <MultiSelect
                options={DETAIL_DIM_OPTIONS.map(option => ({ value: option.key, label: option.label }))}
                value={detailDims}
                onChange={handleDetailDimensionsChange}
                countLabelFormatter={count => `已选 ${count} 个维度`}
                searchable
                tagDisplay
                style={{ width: 360 }}
              />
            </div>
          </div>
          <div className="table-wrap" style={{ position: 'relative', maxHeight: 480, overflow: 'auto' }}>
            <table style={{ minWidth: 1100 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <tr>
                  {detailDims.map(dim => (
                    <SortableHeader key={dim} sortKey={dim} sort={detailSort} onSort={toggleDetailSort} style={thBase}>
                      {detailDimLabel(dim)}
                    </SortableHeader>
                  ))}
                  {DETAIL_COLS.map(column => (
                    <SortableHeader
                      key={column.key}
                      sortKey={column.key as string}
                      sort={detailSort}
                      onSort={toggleDetailSort}
                      align="right"
                      disabled={userLevel < 2 && isSiteSensitiveMetric(column.key as string)}
                      style={{ ...thBase, ...tdNum }}
                    >
                      <MetricDescriptionTooltip label={column.label} />
                    </SortableHeader>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!detailActivated ? <tr><td colSpan={DETAIL_COLS.length + detailDims.length} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>滚动到明细表后加载</td></tr>
                  : detailLoading && detail.length === 0 ? <tr><td colSpan={DETAIL_COLS.length + detailDims.length}><TableLoadingCell /></td></tr>
                  : detail.length === 0 ? <tr><td colSpan={DETAIL_COLS.length + detailDims.length} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</td></tr>
                  : detail.map((row, index) => (
                    <tr key={index}>
                      {detailDims.map(dim => <td key={dim} style={{ whiteSpace: 'nowrap' }}>{detailDimValue(row, dim)}</td>)}
                      {DETAIL_COLS.map(column => <td key={column.key} style={tdNum}>{formatSiteMetric(column.key as string, row[column.key] as number, column.fmt, userLevel)}</td>)}
                    </tr>
                  ))}
              </tbody>
            </table>
            {detailLoading && detail.length > 0 && <TableLoadingOverlay />}
          </div>
          {detailActivated && (
            <PaginationBar
              page={detailPage}
              pageSize={detailPageSize}
              total={detailTotal}
              onPageChange={setDetailPage}
              onPageSizeChange={nextPageSize => { setDetailPageSize(nextPageSize); setDetailPage(1); }}
              loading={detailLoading}
            />
          )}
        </Card>
      </div>

      {/* 花费 - 渠道总 / 分层合计 / 分层明细（复用顶部筛选与日期） */}
      <div ref={costSectionRef}>
        <Card title="花费（净销售额与花费均为美元）">
          <aside className="data-update-note" aria-labelledby="channel-acquisition-cost-note-title" style={{ margin: '4px 16px 8px' }}>
            <h2 id="channel-acquisition-cost-note-title">说明</h2>
            <div className="data-update-note-content">
              <p>1、本看板中新客都是指统计周期内的新客。</p>
              <p>2、对新客销售额的渠道归因，是这个新客在统计周期内的所有订单都归到新客首次订单的渠道里。与其他看板有区别，请注意。</p>
              <p>3、支付用户数的渠道是按照每笔订单的实际渠道进行划分的。</p>
              <p>4、以下报表数据仅受日期维度和日期范围影响，筛选条件不作用于下方表格。</p>
            </div>
          </aside>
          <div style={{ padding: '4px 16px 12px', fontSize: 12, color: 'var(--text3)' }}>
            日粒度总计是按日去重后直接相加；google_ads_other 不统计花费数据。
          </div>
        </Card>
        <CostTable
          title="花费 - 渠道总（三级渠道）"
          dimLabel="三级渠道"
          rows={cost}
          loading={costLoading}
          activated={costActivated}
          page={costPages.cost}
          pageSize={costPageSizes.cost}
          total={costTotals.cost}
          error={costError}
          onPageChange={page => setCostPages(prev => ({ ...prev, cost: page }))}
          onPageSizeChange={pageSize => { setCostPageSizes(prev => ({ ...prev, cost: pageSize })); setCostPages(prev => ({ ...prev, cost: 1 })); }}
          sort={costSorts.cost}
          onSort={key => toggleCostSort('cost', key)}
          extra={<ReportExportButton reportKey="site_channel_acq_cost" title="花费-渠道总" fileName={`花费_渠道总_${costExportSnapshot?.params.start_date ?? ''}_${costExportSnapshot?.params.end_date ?? ''}.csv`} params={{ ...costExportSnapshot?.params, kind: 'cost', ...tableSortParams(costExportSnapshot?.sorts.cost ?? null) }} columns={acqCostExportColumns('三级渠道')} disabled={!costExportSnapshot} />}
          userLevel={userLevel}
        />
        <CostTable
          title="花费 - 分层合计"
          dimLabel="分层"
          rows={layered}
          loading={costLoading}
          activated={costActivated}
          page={costPages.layered}
          pageSize={costPageSizes.layered}
          total={costTotals.layered}
          error={costError}
          onPageChange={page => setCostPages(prev => ({ ...prev, layered: page }))}
          onPageSizeChange={pageSize => { setCostPageSizes(prev => ({ ...prev, layered: pageSize })); setCostPages(prev => ({ ...prev, layered: 1 })); }}
          sort={costSorts.layered}
          onSort={key => toggleCostSort('layered', key)}
          extra={<ReportExportButton reportKey="site_channel_acq_cost" title="花费-分层合计" fileName={`花费_分层合计_${costExportSnapshot?.params.start_date ?? ''}_${costExportSnapshot?.params.end_date ?? ''}.csv`} params={{ ...costExportSnapshot?.params, kind: 'layered', ...tableSortParams(costExportSnapshot?.sorts.layered ?? null) }} columns={acqCostExportColumns('分层')} disabled={!costExportSnapshot} />}
          userLevel={userLevel}
        />
        <CostTable
          title="花费 - 分层明细"
          dimLabel="分层"
          rows={costDetail}
          loading={costLoading}
          activated={costActivated}
          page={costPages.detail}
          pageSize={costPageSizes.detail}
          total={costTotals.detail}
          error={costError}
          onPageChange={page => setCostPages(prev => ({ ...prev, detail: page }))}
          onPageSizeChange={pageSize => { setCostPageSizes(prev => ({ ...prev, detail: pageSize })); setCostPages(prev => ({ ...prev, detail: 1 })); }}
          sort={costSorts.detail}
          onSort={key => toggleCostSort('detail', key)}
          extra={<ReportExportButton reportKey="site_channel_acq_cost" title="花费-分层明细" fileName={`花费_分层明细_${costExportSnapshot?.params.start_date ?? ''}_${costExportSnapshot?.params.end_date ?? ''}.csv`} params={{ ...costExportSnapshot?.params, kind: 'detail', ...tableSortParams(costExportSnapshot?.sorts.detail ?? null) }} columns={acqCostExportColumns('分层')} disabled={!costExportSnapshot} />}
          userLevel={userLevel}
        />
      </div>
    </div>
  );
}

function ChannelSelect({ label, field, value, options, loading = false, error = '', exclude = false, onExcludeChange, onChange }: {
  label: string;
  field: string;
  value: string;
  options: string[];
  loading?: boolean;
  error?: string;
  exclude?: boolean;
  onExcludeChange?: (exclude: boolean) => void;
  onChange: (v: string) => void;
}) {
  void options;
  void loading;
  void error;
  return (
    <label className="data-filter-field lg">
      <span className="data-filter-label">{label}</span>
      <ReportLabelSelect dataset="channel_taxonomy" field={field} pageFields={['one_level_channel', 'two_level_channel', 'three_level_channel', 'pub_zl_utm_source', 'pub_zl_utm_medium']} includeAll value={value === '全部' ? ['全部'] : value.split(',').filter(Boolean)} onChange={next => onChange(next.includes('全部') ? '全部' : next.join(','))} exclude={exclude} onExcludeChange={onExcludeChange} allowExcludeAll paginated={field === 'pub_zl_utm_source' || field === 'pub_zl_utm_medium'} width="100%" />
    </label>
  );
}

function UnifiedFilterRow({
  filters,
  options,
  optionsLoading,
  optionsError,
  onChange,
  exclusions,
  onExclusionsChange,
}: {
  filters: UnifiedAcqFilters;
  options: AcqChannelOptions;
  optionsLoading: boolean;
  optionsError: string;
  onChange: (v: UnifiedAcqFilters) => void;
  exclusions: AcqFilterExclusions;
  onExclusionsChange: Dispatch<SetStateAction<AcqFilterExclusions>>;
}) {
  const setValue = (key: keyof UnifiedAcqFilters, value: string) => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <div className="data-filter-row compact">
      <ChannelSelect label="一级渠道" field="one_level_channel" value={filters.one_level_channel} options={options.one_level} loading={optionsLoading} error={optionsError} exclude={Boolean(exclusions.one_level_channel)} onExcludeChange={exclude => onExclusionsChange(prev => ({ ...prev, one_level_channel: exclude }))} onChange={v => setValue('one_level_channel', v)} />
      <ChannelSelect label="二级渠道" field="two_level_channel" value={filters.two_level_channel} options={options.two_level} loading={optionsLoading} error={optionsError} exclude={Boolean(exclusions.two_level_channel)} onExcludeChange={exclude => onExclusionsChange(prev => ({ ...prev, two_level_channel: exclude }))} onChange={v => setValue('two_level_channel', v)} />
      <ChannelSelect label="三级渠道" field="three_level_channel" value={filters.three_level_channel} options={options.three_level} loading={optionsLoading} error={optionsError} exclude={Boolean(exclusions.three_level_channel)} onExcludeChange={exclude => onExclusionsChange(prev => ({ ...prev, three_level_channel: exclude }))} onChange={v => setValue('three_level_channel', v)} />
      <ChannelSelect label="来源" field="pub_zl_utm_source" value={filters.source} options={options.source} loading={optionsLoading} error={optionsError} exclude={Boolean(exclusions.source)} onExcludeChange={exclude => onExclusionsChange(prev => ({ ...prev, source: exclude }))} onChange={v => setValue('source', v)} />
      <ChannelSelect label="媒介" field="pub_zl_utm_medium" value={filters.medium} options={options.medium} loading={optionsLoading} error={optionsError} exclude={Boolean(exclusions.medium)} onExcludeChange={exclude => onExclusionsChange(prev => ({ ...prev, medium: exclude }))} onChange={v => setValue('medium', v)} />
      <FilterTextInput label="Campaign" value={filters.campaign} onChange={v => setValue('campaign', v)} />
    </div>
  );
}

function FilterTextInput({ label, value, onChange }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="data-filter-field lg">
      <span className="data-filter-label">{label}</span>
      <input
        className="form-input data-filter-input lg"
        value={value === '全部' ? '' : value}
        onChange={e => onChange(e.target.value)}
        placeholder="全部"
      />
    </label>
  );
}

function PaginationBar({ page, pageSize, total, onPageChange, onPageSizeChange, loading }: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  loading: boolean;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  return <Pagination page={safePage} totalPages={totalPages} onChange={onPageChange} total={`共 ${total.toLocaleString('en-US')} 条`} pageSize={pageSize} pageSizeOptions={[20, 50, 100]} onPageSizeChange={onPageSizeChange} disabled={loading} />;
}

function CostTable({ title, dimLabel, rows, loading, activated, page, pageSize, total, error, onPageChange, onPageSizeChange, sort, onSort, extra, userLevel }: {
  title: string;
  dimLabel: string;
  rows: AcqCostRow[];
  loading: boolean;
  activated: boolean;
  page: number;
  pageSize: number;
  total: number;
  error: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  sort: SortState;
  onSort: (key: string) => void;
  extra?: ReactNode;
  userLevel: number;
}) {
  return (
    <Card title={title} extra={extra}>
      <div className="table-wrap" style={{ position: 'relative', maxHeight: 480, overflow: 'auto' }}>
        <table style={{ minWidth: 1500 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>
              <SortableHeader sortKey="dim" sort={sort} onSort={onSort} style={thBase}>{dimLabel}</SortableHeader>
              <SortableHeader sortKey="date" sort={sort} onSort={onSort} style={thBase}>日期</SortableHeader>
              {COST_COLS.map(c => (
                <SortableHeader
                  key={c.key}
                  sortKey={c.key as string}
                  sort={sort}
                  onSort={onSort}
                  align="right"
                  disabled={userLevel < 2 && isSiteSensitiveMetric(c.key as string)}
                  style={{ ...thBase, ...tdNum }}
                >
                  <MetricDescriptionTooltip label={c.label} />
                </SortableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {!activated ? <tr><td colSpan={COST_COLS.length + 2} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>滚动到花费区域后加载</td></tr>
              : loading && rows.length === 0 ? <tr><td colSpan={COST_COLS.length + 2}><TableLoadingCell /></td></tr>
              : error ? <tr><td colSpan={COST_COLS.length + 2} style={{ textAlign: 'center', padding: 40, color: '#dc2626' }}>{error}</td></tr>
              : rows.length === 0 ? <tr><td colSpan={COST_COLS.length + 2} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</td></tr>
              : rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.dim}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.date}</td>
                  {COST_COLS.map(c => <td key={c.key} style={tdNum}>{formatSiteMetric(c.key as string, r[c.key] as number, c.fmt, userLevel)}</td>)}
                </tr>
              ))}
          </tbody>
        </table>
        {loading && rows.length > 0 && <TableLoadingOverlay />}
      </div>
      {activated && (
        <PaginationBar
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          loading={loading}
        />
      )}
    </Card>
  );
}
